import React from "react";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Skeleton loader for detail pages (Appointment, Lead, Client)
 * Provides a polished, modern loading UX with animated placeholders
 */
export function DetailPageSkeleton({ variant = "default" }) {
  return (
    <div className="space-y-6" data-testid="detail-page-skeleton">
      {/* Back button + Action buttons skeleton */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-9 w-24 rounded-full bg-white/10" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-28 rounded-full bg-white/10" />
          <Skeleton className="h-9 w-24 rounded-full bg-white/10" />
        </div>
      </div>

      {/* Main content card skeleton */}
      <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
        {/* Header section */}
        <div className="mb-6 flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-6 w-48 bg-white/10" />
            <Skeleton className="h-4 w-32 bg-white/10" />
          </div>
          <Skeleton className="h-8 w-8 rounded-full bg-white/10" />
        </div>

        {/* Info grid skeleton */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-3 w-20 bg-white/10" />
              <Skeleton className="h-5 w-full bg-white/10" />
            </div>
          ))}
        </div>
      </div>

      {/* Two column layout skeleton */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Left column */}
        <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
          <div className="mb-4 flex items-center justify-between">
            <Skeleton className="h-5 w-32 bg-white/10" />
            <Skeleton className="h-8 w-8 rounded-full bg-white/10" />
          </div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg bg-white/5 p-3">
                <div className="space-y-1">
                  <Skeleton className="h-4 w-28 bg-white/10" />
                  <Skeleton className="h-3 w-20 bg-white/10" />
                </div>
                <Skeleton className="h-6 w-16 rounded-full bg-white/10" />
              </div>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
          <div className="mb-4 flex items-center justify-between">
            <Skeleton className="h-5 w-24 bg-white/10" />
            <div className="flex gap-2">
              <Skeleton className="h-8 w-8 rounded-full bg-white/10" />
              <Skeleton className="h-8 w-8 rounded-full bg-white/10" />
            </div>
          </div>
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-32 bg-white/10" />
                    <Skeleton className="h-3 w-24 bg-white/10" />
                  </div>
                  <Skeleton className="h-6 w-14 rounded-full bg-white/10" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom section skeleton */}
      {variant === "appointment" && (
        <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
          <div className="mb-4 flex items-center justify-between">
            <Skeleton className="h-5 w-28 bg-white/10" />
            <Skeleton className="h-8 w-8 rounded-full bg-white/10" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-3 w-24 bg-white/10" />
                <Skeleton className="h-5 w-full bg-white/10" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact skeleton for smaller detail sections
 */
export function SectionSkeleton({ rows = 4 }) {
  return (
    <div className="space-y-3" data-testid="section-skeleton">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex items-center justify-between">
          <Skeleton className="h-4 w-24 bg-white/10" />
          <Skeleton className="h-4 w-32 bg-white/10" />
        </div>
      ))}
    </div>
  );
}

/**
 * Table skeleton for data tables
 */
export function TableSkeleton({ rows = 5, cols = 4 }) {
  return (
    <div className="space-y-2" data-testid="table-skeleton">
      {/* Header */}
      <div className="flex gap-4 border-b border-white/10 pb-2">
        {[...Array(cols)].map((_, i) => (
          <Skeleton key={i} className="h-4 flex-1 bg-white/10" />
        ))}
      </div>
      {/* Rows */}
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex gap-4 py-2">
          {[...Array(cols)].map((_, j) => (
            <Skeleton key={j} className="h-4 flex-1 bg-white/10" />
          ))}
        </div>
      ))}
    </div>
  );
}

export default DetailPageSkeleton;
